import React, { useMemo, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Search, Check, Clock, Utensils, Sunrise, Coffee, Moon, Pizza, Cake, Edit3, ChefHat, Power } from 'lucide-react';
import { useMenus, Menu, catalogItems, menuIconKeys } from './MenusContext';
import { Toggle } from './Toggle';
import { ScheduleModal, formatSchedule } from './ScheduleModal';
import { useBranches } from './BranchesContext';
import { StationIconBadge } from './BranchStationsSection';
const iconMap: Record<string, ComponentType<{
  size?: number;
}>> = {
  utensils: Utensils,
  sunrise: Sunrise,
  coffee: Coffee,
  moon: Moon,
  pizza: Pizza,
  cake: Cake
};
const colorClasses: Record<string, {
  bg: string;
  text: string;
  ring: string;
  iconBg: string;
  gradient: string;
}> = {
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    ring: 'ring-orange-200',
    iconBg: 'bg-orange-100 text-orange-600',
    gradient: 'from-orange-50 to-orange-100'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    iconBg: 'bg-amber-100 text-amber-600',
    gradient: 'from-amber-50 to-amber-100'
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    ring: 'ring-indigo-200',
    iconBg: 'bg-indigo-100 text-indigo-600',
    gradient: 'from-indigo-50 to-indigo-100'
  },
  pink: {
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    ring: 'ring-pink-200',
    iconBg: 'bg-pink-100 text-pink-600',
    gradient: 'from-pink-50 to-pink-100'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    iconBg: 'bg-emerald-100 text-emerald-600',
    gradient: 'from-emerald-50 to-emerald-100'
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    ring: 'ring-blue-200',
    iconBg: 'bg-blue-100 text-blue-600',
    gradient: 'from-blue-50 to-blue-100'
  },
  purple: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    ring: 'ring-purple-200',
    iconBg: 'bg-purple-100 text-purple-600',
    gradient: 'from-purple-50 to-purple-100'
  },
  rose: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    ring: 'ring-rose-200',
    iconBg: 'bg-rose-100 text-rose-600',
    gradient: 'from-rose-50 to-rose-100'
  }
};
export function MenusPanel() {
  const {
    menus,
    addMenu,
    updateMenu,
    removeMenu,
    toggleItem
  } = useMenus();
  const {
    branches
  } = useBranches();
  const [selectedId, setSelectedId] = useState<string>(menus[0]?.id || '');
  const [search, setSearch] = useState('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  // Pool stations across branches
  const stationMap = useMemo(() => {
    const m: Record<string, {
      id: string;
      name: string;
      color: string;
      icon: string;
    }> = {};
    branches.forEach(b => b.stations.forEach(s => {
      if (!m[s.id]) m[s.id] = s;
    }));
    return m;
  }, [branches]);
  const selected = menus.find(m => m.id === selectedId);
  // Count which branches default to this menu
  const branchesUsingSelected = useMemo(() => {
    if (!selected) return [] as string[];
    return branches.filter(b => b.defaultMenuId === selected.id).map(b => b.name);
  }, [branches, selected]);
  const filteredCatalog = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return catalogItems;
    return catalogItems.filter(i => i.name.toLowerCase().includes(q) || i.category.toLowerCase().includes(q));
  }, [search]);
  const handleAdd = () => {
    const id = addMenu({
      name: 'New menu'
    });
    setSelectedId(id);
    setRenamingId(id);
  };
  return <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 md:gap-8 items-start" data-id="element-2400">
      {/* Left pane: list of menus */}
      <div className="flex flex-col gap-3" data-id="element-2401">
        <div className="flex items-center justify-between mb-1" data-id="element-2402">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-2403">Menus</h2>
          <button onClick={handleAdd} className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-2404">
            <Plus size={14} data-id="element-2405" />
            Add Menu
          </button>
        </div>
        <p className="text-xs text-gray-500 -mt-1 mb-1" data-id="element-2406">
          Group items into menus and schedule when each is active. Each branch
          picks a default menu.
        </p>

        <div className="flex flex-col gap-3" data-id="element-2407">
          {menus.map(menu => {
          const c = colorClasses[menu.color] || colorClasses.orange;
          const Icon = iconMap[menu.icon] || Utensils;
          const isSelected = menu.id === selectedId;
          const usedByCount = branches.filter(b => b.defaultMenuId === menu.id).length;
          return <motion.div key={menu.id} layout onClick={() => setSelectedId(menu.id)} className={`p-4 rounded-xl border cursor-pointer transition-all ${isSelected ? `${c.bg} border-transparent ring-2 ${c.ring} shadow-sm` : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`} data-id="element-2408">
                <div className="flex items-start justify-between gap-3" data-id="element-2409">
                  <div className="flex items-start gap-3 min-w-0" data-id="element-2410">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.iconBg}`} data-id="element-2411">
                      <Icon size={18} data-id="element-2412" />
                    </div>
                    <div className="min-w-0" data-id="element-2413">
                      <div className={`font-bold text-sm ${isSelected ? 'text-gray-900' : 'text-gray-800'}`} data-id="element-2414">
                        {menu.name}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1" data-id="element-2415">
                        {menu.description || 'No description'}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px]" data-id="element-2416">
                        <span className="inline-flex items-center gap-1 text-gray-600" data-id="element-2417">
                          <Utensils size={10} data-id="element-2418" />
                          {menu.itemIds.length} item
                          {menu.itemIds.length === 1 ? '' : 's'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-gray-600" data-id="element-2419">
                          <Clock size={10} data-id="element-2420" />
                          {menu.schedule.alwaysAvailable ? 'Always' : formatSchedule(menu.schedule)}
                        </span>
                        {usedByCount > 0 && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${c.bg} ${c.text} font-semibold`} data-id="element-2421">
                            <ChefHat size={10} data-id="element-2422" />
                            {usedByCount} branch
                            {usedByCount === 1 ? '' : 'es'}
                          </span>}
                      </div>
                    </div>
                  </div>
                  <div onClick={e => e.stopPropagation()} data-id="element-2423">
                    <Toggle checked={menu.active} onChange={v => updateMenu(menu.id, {
                  active: v
                })} data-id="element-2424" />
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Right pane: detail */}
      {selected ? <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2425">
          {/* Header */}
          <div className={`bg-gradient-to-br ${colorClasses[selected.color]?.gradient || 'from-orange-50 to-orange-100'} px-6 py-5 border-b border-gray-200`} data-id="element-2426">
            <div className="flex items-start justify-between gap-3" data-id="element-2427">
              <div className="flex items-start gap-3 min-w-0" data-id="element-2428">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClasses[selected.color]?.iconBg || 'bg-orange-100 text-orange-600'}`} data-id="element-2429">
                  {createElement(iconMap[selected.icon] || Utensils, {
                size: 22
              })}
                </div>
                <div className="min-w-0 flex-1" data-id="element-2430">
                  {renamingId === selected.id ? <input autoFocus type="text" value={selected.name} onChange={e => updateMenu(selected.id, {
                name: e.target.value
              })} onBlur={() => setRenamingId(null)} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur();
              }} className="w-full px-2 py-1 rounded-md border border-orange-300 bg-white text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-100" data-id="element-2431" /> : <button onClick={() => setRenamingId(selected.id)} className="text-left flex items-center gap-2 group" data-id="element-2432">
                      <h2 className="text-lg font-bold text-gray-900" data-id="element-2433">
                        {selected.name}
                      </h2>
                      <Edit3 size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2434" />
                    </button>}
                  <input type="text" value={selected.description} placeholder="Add a short description" onChange={e => updateMenu(selected.id, {
                description: e.target.value
              })} className="w-full mt-1 px-0 py-0.5 bg-transparent border-0 text-sm text-gray-600 focus:outline-none focus:ring-0 placeholder:text-gray-400" data-id="element-2435" />
                </div>
              </div>
              <button onClick={() => {
            if (confirm(`Remove "${selected.name}"? Branches using it will fall back to the main menu.`)) {
              removeMenu(selected.id);
              setSelectedId(menus.find(m => m.id !== selected.id)?.id || '');
            }
          }} className="p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-white/70 transition-colors flex-shrink-0" data-id="element-2436">
                <Trash2 size={16} data-id="element-2437" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-2438">
              <button onClick={() => setScheduleModalOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-2439">
                <Clock size={12} data-id="element-2440" />
                {selected.schedule.alwaysAvailable ? 'Always available' : formatSchedule(selected.schedule)}
              </button>
              <div className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white border border-gray-200" data-id="element-2441">
                {menuIconKeys.map(k => {
              const I = iconMap[k] || Utensils;
              const active = selected.icon === k;
              return <button key={k} onClick={() => updateMenu(selected.id, {
                icon: k
              })} className={`p-1 rounded-md transition-colors ${active ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-700'}`} data-id="element-2442">
                      <I size={12} data-id="element-2443" />
                    </button>;
            })}
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200" data-id="element-2444">
                <Power size={12} className="text-gray-500" data-id="element-2445" />
                <span className="text-xs font-semibold text-gray-700" data-id="element-2446">
                  {selected.active ? 'Active' : 'Inactive'}
                </span>
                <Toggle checked={selected.active} onChange={v => updateMenu(selected.id, {
              active: v
            })} data-id="element-2447" />
              </div>
              {branchesUsingSelected.length > 0 && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-gray-700" data-id="element-2448">
                  <ChefHat size={12} className="text-emerald-600" data-id="element-2449" />
                  Default at: {branchesUsingSelected.join(', ')}
                </span>}
            </div>
          </div>

          {/* Items selector */}
          <div className="p-6" data-id="element-2450">
            <div className="flex items-center justify-between mb-3" data-id="element-2451">
              <h3 className="text-sm font-bold text-gray-900" data-id="element-2452">
                Items in this menu
              </h3>
              <span className="text-xs font-semibold text-gray-500" data-id="element-2453">
                {selected.itemIds.length} of {catalogItems.length} selected
              </span>
            </div>

            <div className="relative mb-4" data-id="element-2454">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2455" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items by name or category" className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-2456" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-2457">
              {filteredCatalog.map(item => {
            const included = selected.itemIds.includes(item.id);
            const station = stationMap[item.stationId];
            return <button key={item.id} onClick={() => toggleItem(selected.id, item.id)} className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${included ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-2458">
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${included ? 'bg-emerald-500 border-2 border-emerald-500' : 'border-2 border-gray-300 bg-white'}`} data-id="element-2459">
                      {included && <Check size={12} strokeWidth={3} className="text-white" data-id="element-2460" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2461">
                      <div className="text-sm font-bold text-gray-900 truncate" data-id="element-2462">
                        {item.name}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate" data-id="element-2463">
                        {item.category} · ETB {item.price}
                      </div>
                    </div>
                    {station && <StationIconBadge icon={station.icon} color={station.color} size={11} data-id="element-2464" />}
                  </button>;
          })}
            </div>
            {filteredCatalog.length === 0 && <div className="text-center py-8 text-sm text-gray-500" data-id="element-2465">
                No items match "{search}".
              </div>}
          </div>

          {/* Schedule modal */}
          <AnimatePresence data-id="element-2466">
            {scheduleModalOpen && <ScheduleModal initial={selected.schedule} onClose={() => setScheduleModalOpen(false)} onSave={schedule => {
          updateMenu(selected.id, {
            schedule
          });
          setScheduleModalOpen(false);
        }} data-id="element-2467" />}
          </AnimatePresence>
        </div> : <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-id="element-2468">
          <ChefHat size={32} className="mx-auto text-gray-400 mb-3" strokeWidth={1.5} data-id="element-2469" />
          <h3 className="text-base font-bold text-gray-700 mb-1" data-id="element-2470">
            No menu selected
          </h3>
          <p className="text-sm text-gray-500" data-id="element-2471">
            Pick a menu on the left or create a new one to get started.
          </p>
        </div>}
    </div>;
}